import React from 'react';
import HelpArticleLayout, { UiLabel, InfoCallout } from '../components/HelpArticleLayout';

// Help article: "How do briefs work?" — on the BRIEF-27 sections template.

const ICON = {
  bell:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>,
  doc:    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
  reply:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>,
  clock:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  trophy: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4zM17 5h2a2 2 0 0 1-2 3.5M7 5H5a2 2 0 0 0 2 3.5" /></svg>,
  info:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></svg>,
  list:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
};

const sections = [
  {
    id: 'notice', title: 'How you’ll know you’ve got a brief', icon: ICON.bell,
    body: (
      <>
        <p>Two things happen:</p>
        <ol className="list-decimal pl-5 space-y-2 marker:text-fusion-forest marker:font-bold">
          <li>You&rsquo;ll get an email from Fusion telling you a new brief is waiting for you.</li>
          <li>The brief shows up in your portal under <UiLabel>Your briefs</UiLabel> on the <UiLabel>Awaiting you</UiLabel> tab.</li>
        </ol>
        <p>Briefs come from Fusion 2K&rsquo;s project managers when a client (Renault, Kia, agencies, and others) has a piece of work that matches what you do.</p>
      </>
    ),
  },
  {
    id: 'contents', title: 'What’s in a brief', icon: ICON.doc,
    body: (
      <>
        <p>Each brief tells you what Fusion needs:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-fusion-forest">
          <li>What the work is (description, scope, deliverables)</li>
          <li>When it&rsquo;s happening (event date, on-site times)</li>
          <li>Where it&rsquo;s happening (venue, address)</li>
          <li>How many people are involved (headcount, attendees)</li>
          <li>Any specific requirements (branding, dietary needs, technical specs)</li>
          <li>When the response is due</li>
        </ul>
        <p>You&rsquo;ll also see who at Fusion 2K is running the brief, so you know who&rsquo;s expecting your reply.</p>
      </>
    ),
  },
  {
    id: 'respond', title: 'Responding to a brief', icon: ICON.reply,
    body: (
      <>
        <p>From the brief detail page you can:</p>
        <ol className="list-decimal pl-5 space-y-2 marker:text-fusion-forest marker:font-bold">
          <li><strong className="text-ink">Submit a quote</strong> — your price, what&rsquo;s included, any caveats. Attach a PDF quote if you prefer, or fill in the form.</li>
          <li><strong className="text-ink">Ask a question</strong> — if anything&rsquo;s unclear, use the questions panel to message the Fusion contact directly.</li>
          <li><strong className="text-ink">Decline politely</strong> — if you can&rsquo;t do it (out of capacity, outside your service area, conflict of interest, etc.), let Fusion know with a quick reason. This actually helps — it stops Fusion chasing, and keeps your record honest.</li>
        </ol>
        <InfoCallout><strong className="text-ink">Deadlines matter.</strong> If your response misses the deadline, Fusion may have already awarded the work. Quotes can usually be revised right up until award, so it&rsquo;s better to submit something rough than nothing at all.</InfoCallout>
      </>
    ),
  },
  {
    id: 'after', title: 'What happens after you respond', icon: ICON.clock,
    body: (
      <>
        <p>Once your quote is in, Fusion reviews it alongside any other supplier responses. The brief moves through these stages:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-fusion-forest">
          <li><strong className="text-ink">Responses in</strong> — Fusion 2K is comparing quotes</li>
          <li><strong className="text-ink">Awarded</strong> — one supplier has been chosen. If that&rsquo;s you, you&rsquo;ll get an email confirming.</li>
          <li><strong className="text-ink">Closed</strong> — the brief is complete, often after the event has happened</li>
        </ul>
      </>
    ),
  },
  {
    id: 'won', title: 'When you’ve been awarded', icon: ICON.trophy,
    body: (
      <>
        <p>You&rsquo;ll get an email with the good news. The brief moves to your <UiLabel>Awarded</UiLabel> tab. The Fusion project manager will be in touch shortly to kick off the work — contracts, schedules, on-site logistics. Everything from that point happens off-portal.</p>
        <p>If you&rsquo;ve been awarded by mistake, or your circumstances have changed, contact Fusion immediately so the work can be reassigned.</p>
      </>
    ),
  },
  {
    id: 'not-won', title: 'When you weren’t awarded', icon: ICON.info,
    body: (
      <p>Not every brief leads to a win — that&rsquo;s normal. You&rsquo;ll see the brief move to <UiLabel>Closed</UiLabel> with an outcome of either &ldquo;awarded to another supplier&rdquo; or &ldquo;closed without award&rdquo;. No bad feeling — Fusion 2K&rsquo;s deciding based on a mix of price, fit, availability, and (sometimes) supplier badges.</p>
    ),
  },
  {
    id: 'history', title: 'Tracking your history', icon: ICON.list,
    body: (
      <>
        <p>The tabs under <UiLabel>Your briefs</UiLabel> give you a full record:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-fusion-forest">
          <li><strong className="text-ink">Awaiting you</strong> — briefs you need to act on</li>
          <li><strong className="text-ink">In progress</strong> — briefs you&rsquo;ve responded to but haven&rsquo;t been awarded yet</li>
          <li><strong className="text-ink">Awarded</strong> — briefs you&rsquo;ve won (current and past)</li>
          <li><strong className="text-ink">Closed</strong> — briefs that closed without you winning, plus completed past work</li>
        </ul>
      </>
    ),
  },
];

export default function HelpHowBriefsWork() {
  return (
    <HelpArticleLayout
      breadcrumb="Working with Fusion 2K"
      title="How do briefs work?"
      intro="A brief is Fusion 2K&rsquo;s way of asking you to quote on a piece of work. From receiving one to getting awarded, here&rsquo;s the whole journey."
      readTime="4 min read"
      sections={sections}
      footerText="Got a question on a specific brief? The fastest route is the questions panel inside that brief — it goes straight to the project manager."
    />
  );
}
